import { useCallback, useEffect, useRef, useState } from 'react';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import {
  BimDataModelProgress,
  BimEntityProperties,
  BimMeasureMode,
  BimMeasurement,
  BimProjectionMode,
  BimSectionPlane,
  BimSelection,
  BimTreeNode,
  BimViewerHandle,
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { createBimViewerAdapter } from 'Data/adapters/BimViewer';
import { IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { apiNameForTracking, COORDLY_3D_VIEWER_EXTENSION } from 'Utils/ViewerUtils';
import { Mixpanel } from 'Utils/MixPanel';
import { resolveUrnToFile } from '../../federationUrn';
import { INativeModelResult, prepareNativeModel } from '../../nativeModel';
import { bimEntitiesToProperties, BimPanelProperties } from '../../ifcProperties';

/**
 * Teto de elementos agregados, o mesmo do viewer 3D: um nó de árvore cobre
 * milhares de elementos, e agregar tudo trava a UI por um painel que ninguém
 * leria linha a linha.
 */
const MAX_AGGREGATED_PROPERTIES = 200;

interface IUseFederatedIfcViewer {
  viewerDiv: HTMLElement | null;
  urns: string[];
  viewerListDisciplines: IDisciplineViewerList[];
  constructionSiteId: number;
  onEngineError?(detail: { code: string; message: string }): void;
  // O modelo não entrou na cena. O evento de erro do motor não diz de qual
  // modelo se trata, mas aqui a identidade é conhecida — sem isso o urn segue em
  // `urns` e o switch fica ligado sem nada na cena.
  onModelLoadFailed?(urn: string): void;
  // Urn em carga, ou null quando o motor está livre. A lista usa isso para
  // travar os switches durante a carga — o equivalente ao `loadUrn` da Autodesk.
  onLoadingUrnChange?(urn: string | null): void;
  // Propriedades do elemento selecionado, já no formato do painel do federado.
  // `null` quando a seleção é limpa.
  onSelectedProperties?(properties: BimPanelProperties | null): void;
  // Árvore espacial de UM modelo, quando o data model dele fica pronto (chega
  // depois do render). A lista desenha uma árvore por modelo ligado.
  onModelTree?(urn: string, tree: BimTreeNode[], modelIndex?: number): void;
  // O handle é o que a árvore usa para selecionar no 3D e materializar folhas.
  // `null` no unmount.
  onHandleReady?(handle: BimViewerHandle | null): void;
  // Repasses crus do motor para o `useBim3DTools`, que mantém o estado das
  // ferramentas (isolar, ocultar, corte, medição). O hook daqui continua
  // cuidando só da cena federada.
  onSelection?(selection: BimSelection): void;
  onMeasure?(state: { mode: BimMeasureMode; measurements: BimMeasurement[] }): void;
  /** Esc na cena: o motor voltou ao padrão e as ferramentas precisam zerar. */
  onViewReset?(): void;
  onProjection?(mode: BimProjectionMode): void;
  onSection?(section: BimSectionPlane | null): void;
}

// Hook do viewer IFC federado (paralelo ao useInternalViewer, mas p/ ifc-lite).
// Monta a cena vazia via porta e adiciona um modelo por urn IFC ativo. Não muta
// Redux — só lê `urns`/`viewerListDisciplines`. A porta isola o motor.
export const useFederatedIfcViewer = ({
  viewerDiv,
  urns,
  viewerListDisciplines,
  constructionSiteId,
  onEngineError,
  onModelLoadFailed,
  onLoadingUrnChange,
  onSelectedProperties,
  onModelTree,
  onHandleReady,
  onSelection,
  onMeasure,
  onViewReset,
  onProjection,
  onSection,
}: IUseFederatedIfcViewer) => {
  const handleRef = useRef<BimViewerHandle | null>(null);
  const loadedRef = useRef<Set<string>>(new Set());
  // Início do addModel por urn, para o tempo até a árvore/propriedades.
  const dataModelStartRef = useRef<Map<string, { startedAt: number; sizeInBytes?: number; apiName: string | null }>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'download' | 'parse'>('idle');
  // Os modelos entram um por vez, então basta o andamento do último que falou.
  const [dataModelProgress, setDataModelProgress] = useState<BimDataModelProgress | null>(null);

  // Refs p/ o processUrns ficar estável (sem re-montar quando a lista muda).
  const urnsRef = useRef(urns);
  urnsRef.current = urns;
  const dataRef = useRef({ viewerListDisciplines, constructionSiteId });
  dataRef.current = { viewerListDisciplines, constructionSiteId };
  const onErrorRef = useRef(onEngineError);
  onErrorRef.current = onEngineError;
  const onModelFailedRef = useRef(onModelLoadFailed);
  onModelFailedRef.current = onModelLoadFailed;
  const onLoadingRef = useRef(onLoadingUrnChange);
  onLoadingRef.current = onLoadingUrnChange;
  const onPropertiesRef = useRef(onSelectedProperties);
  onPropertiesRef.current = onSelectedProperties;
  const onModelTreeRef = useRef(onModelTree);
  onModelTreeRef.current = onModelTree;
  const onHandleReadyRef = useRef(onHandleReady);
  onHandleReadyRef.current = onHandleReady;
  const onSelectionRef = useRef(onSelection);
  onSelectionRef.current = onSelection;
  const onMeasureRef = useRef(onMeasure);
  onMeasureRef.current = onMeasure;
  const onViewResetRef = useRef(onViewReset);
  onViewResetRef.current = onViewReset;
  const onProjectionRef = useRef(onProjection);
  onProjectionRef.current = onProjection;
  const onSectionRef = useRef(onSection);
  onSectionRef.current = onSection;

  // Data model de um modelo ficou pronto: lê a árvore DELE (por `modelIndex`) e
  // entrega para a lista. Sem `modelId` não dá para saber a qual switch a árvore
  // pertence, então não há o que fazer.
  const handleDataModelReady = useCallback((detail: {
    available: boolean;
    modelIndex?: number;
    modelId?: string;
  }) => {
    const handle = handleRef.current;
    if (!detail.available) {
      setDataModelProgress((current) => (current?.modelId === detail.modelId ? null : current));
    }
    if (!handle || !detail.modelId) { return; }
    const start = dataModelStartRef.current.get(detail.modelId);
    if (start !== undefined) {
      dataModelStartRef.current.delete(detail.modelId);
      Mixpanel.track({
        name: 'COORDLY_3D_VIEWER_DATA_MODEL_READY',
        props: {
          at: 'federated',
          available: detail.available,
          sizeInBytes: start.sizeInBytes,
          apiName: start.apiName,
          msDataModel: Math.round(performance.now() - start.startedAt),
        },
      });
    }
    onModelTreeRef.current?.(
      detail.modelId,
      detail.available ? handle.getSpatialTree(detail.modelIndex) : [],
      detail.modelIndex,
    );
  }, []);

  // Clique na cena → propriedades do elemento. O motor manda só a seleção; quem
  // lê os dados é a porta, e só depois do data model chegar (ele vem depois do
  // render). Sem elemento selecionado o painel volta a vazio.
  // As propriedades vêm do worker do data model e podem voltar fora de ordem: só
  // a resposta da seleção mais recente chega ao painel.
  const propertiesRequest = useRef(0);

  const handleSelectionChanged = useCallback(async (selection: BimSelection) => {
    onSelectionRef.current?.(selection);
    const handle = handleRef.current;
    const request = ++propertiesRequest.current;
    // A seleção inteira, não só o último clicado: é o que permite o painel somar
    // as quantidades. `expressId` pode ser null com itens ainda selecionados
    // (deselecionar o último clicado não esvazia a seleção), então quem manda
    // aqui é a lista.
    const ids = selection.expressIds ?? [];
    if (!handle || ids.length === 0) {
      onPropertiesRef.current?.(null);
      return;
    }

    // O modelo é resolvido POR ELEMENTO: o expressId é local ao arquivo, e uma
    // seleção que cruza modelos federados lida com um índice só devolveria o
    // data model errado — com ids que existem nos dois, sem erro nenhum, só
    // números trocados. `modelIndices` ausente = motor antigo, que só sabia
    // reportar o modelo do último clicado. Uma consulta por modelo, não por id.
    const byModel = new Map<number | undefined, number[]>();
    ids.slice(0, MAX_AGGREGATED_PROPERTIES).forEach((expressId, index) => {
      const modelIndex = selection.modelIndices?.[index] ?? selection.modelIndex;
      const group = byModel.get(modelIndex);
      if (group) { group.push(expressId); } else { byModel.set(modelIndex, [expressId]); }
    });

    const perModel = await Promise.all(Array.from(byModel, ([modelIndex, modelIds]) =>
      handle.getEntitiesProperties(modelIds, modelIndex)));
    if (request !== propertiesRequest.current) { return; }

    const entities: BimEntityProperties[] = perModel.flat();
    onPropertiesRef.current?.(bimEntitiesToProperties(entities));
  }, []);

  // Reconcilia a cena com os urns ativos: tira os que saíram, põe os que
  // entraram. Simétrico — ligar e desligar custam o mesmo, e cada modelo é
  // independente dos outros.
  //
  // Antes isto era `clearModels()` + re-add de TODOS os restantes, porque a cena
  // não sabia remover por modelo (`removeMeshesForEntities` é por expressId, e
  // disciplinas reusam ids). Além do custo — desligar 1 de N re-baixava e
  // re-parseava os N-1 —, a janela entre o clear e os re-adds deixava a cena e o
  // `loadedRef` divergirem quando um re-add falhava ou o usuário mexia em outro
  // switch no meio: o modelo desligado voltava, ou sumia o errado. O motor agora
  // dá faixa de id própria a cada modelo e remove só o dele (`removeModel`).
  // Serializa só o que mexe na cena (tirar e pôr modelos): um clear no meio de
  // um addModel em voo deixaria a cena inconsistente. A espera do backend NÃO
  // entra aqui — ela leva minutos no primeiro open, e na fila travava todo
  // switch ligado ou desligado depois dela.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const enqueue = useCallback((task: () => Promise<void>) => {
    queueRef.current = queueRef.current.then(task, task);
    return queueRef.current;
  }, []);

  // Põe na cena um modelo cujos artefatos já estão prontos.
  const addPreparedModel = useCallback(async (
    handle: BimViewerHandle,
    urn: string,
    file: IFileData,
    { artifacts, waited, failure }: INativeModelResult,
    timing: { startedAt: number; msProcessing: number },
  ) => {
    // Viewer desmontado durante a espera: não há cena, e avisar falha agora
    // desligaria switches de uma sessão que nem existe mais.
    if (handleRef.current !== handle) { return; }

    // Só avisa se o urn ainda estiver ativo: desligado no meio da carga, o
    // switch já saiu por ação do usuário e avisar aqui o religaria (SET_URNS
    // é toggle).
    const reportFailure = () => {
      if (urnsRef.current.includes(urn)) { onModelFailedRef.current?.(urn); }
    };
    const trackFailure = (reason: string, failureCode?: string | null) => Mixpanel.track({
      name: 'COORDLY_3D_VIEWER_FEDERATED_MODEL_FAILED',
      props: { reason, failureCode, sizeInBytes: file.SizeInBytes, apiName: apiNameForTracking(file.FileInfo?.Api) },
    });

    if (!artifacts) {
      if (failure) { trackFailure(failure.reason, failure.failureCode); }
      loadedRef.current.delete(urn);
      reportFailure();
      return;
    }
    // Desligado enquanto o backend preparava: nem entra na cena.
    if (!urnsRef.current.includes(urn)) {
      loadedRef.current.delete(urn);
      return;
    }

    // Trava os switches enquanto este modelo carrega. Sem isso dá para ligar
    // vários de uma vez (downloads e decodes concorrentes) e para ligar/desligar
    // o mesmo várias vezes durante a carga — o efeito só aparecia no fim, na
    // última ação que tivesse sobrado.
    onLoadingRef.current?.(urn);
    try {
      let added = true;
      dataModelStartRef.current.set(urn, { startedAt: performance.now(), sizeInBytes: file.SizeInBytes, apiName: apiNameForTracking(file.FileInfo?.Api) });
      try {
        await handle.addModel({ artifacts, fileName: file.Name }, urn);
      } catch {
        added = false;
      }

      // Saiu da página no meio da carga: o motor foi descartado e o modelo não
      // entrou, mas isso não é falha. Avisar falha aqui religava o urn (SET_URNS é
      // toggle) depois de a página já ter limpado a sessão, e a volta carregava
      // tudo de novo.
      if (handleRef.current !== handle) { return; }

      // O motor engole a falha de carga (reporta por onError e desiste do
      // modelo), então a promise resolver não significa que entrou na cena.
      if (!added || !handle.hasModel(urn)) {
        loadedRef.current.delete(urn);
        dataModelStartRef.current.delete(urn);
        trackFailure('add-failed');
        reportFailure();

      // Desligado DURANTE a carga: desfaz aqui, a próxima passada só roda se
      // `urns` mudar de novo.
      } else if (!urnsRef.current.includes(urn)) {
        handle.removeModel(urn);
        loadedRef.current.delete(urn);
        dataModelStartRef.current.delete(urn);
        onModelTreeRef.current?.(urn, []);
      } else {
        Mixpanel.track({
          name: 'COORDLY_3D_VIEWER_LOADED',
          props: {
            at: 'federated',
            sizeInBytes: file.SizeInBytes,
            apiName: apiNameForTracking(file.FileInfo?.Api),
            waited,
            msProcessing: timing.msProcessing,
            msTotal: Math.round(performance.now() - timing.startedAt),
          },
        });
      }
    } finally {
      onLoadingRef.current?.(null);
    }
  }, []);

  // Reconcilia a cena com os urns ativos: tira os que saíram, e para os que
  // entraram dispara a preparação no backend — cada um entra na fila da cena
  // quando fica pronto, na ordem em que fica pronto.
  const runUrns = useCallback(async () => {
    const handle = handleRef.current;
    if (!handle) { return; }
    const { viewerListDisciplines: disciplines, constructionSiteId: csId } = dataRef.current;

    const active = new Set(urnsRef.current);
    for (const urn of Array.from(loadedRef.current)) {
      if (active.has(urn)) { continue; }
      handle.removeModel(urn);
      loadedRef.current.delete(urn);
      // A árvore sai com o modelo: mantê-la desenhada apontaria para geometria
      // que não está mais na cena.
      onModelTreeRef.current?.(urn, []);
    }

    const pending = urnsRef.current
      .filter((urn) => !loadedRef.current.has(urn))
      .map((urn) => ({ urn, file: resolveUrnToFile(urn, disciplines) }))
      .filter((item): item is { urn: string; file: IFileData } => (
        item.file?.Extension?.toLowerCase() === COORDLY_3D_VIEWER_EXTENSION
      ));

    // Marca ANTES da espera pra não duplicar numa segunda passada; se falhar,
    // `addPreparedModel` desmarca — senão o urn nunca mais seria tentado.
    for (const { urn, file } of pending) {
      loadedRef.current.add(urn);
      const isActive = () => handleRef.current === handle && urnsRef.current.includes(urn);
      const startedAt = performance.now();
      void prepareNativeModel(file, csId, isActive)
        .catch((): INativeModelResult => ({
          artifacts: null,
          waited: false,
          failure: { reason: 'native-request-failed' },
        }))
        .then((result) => {
          const timing = { startedAt, msProcessing: Math.round(performance.now() - startedAt) };
          return enqueue(() => addPreparedModel(handle, urn, file, result, timing));
        });
    }
  }, [enqueue, addPreparedModel]);

  const processUrns = useCallback(() => enqueue(runUrns), [enqueue, runUrns]);

  useEffect(() => {
    if (!viewerDiv) { return; }
    let cancelled = false;

    const adapter = createBimViewerAdapter();
    adapter
      .mount(viewerDiv, {}, {
        onLoadProgress: (p) => setPhase(p === 'download' ? 'download' : 'parse'),
        onLoaded: () => { setPhase('idle'); setLoaded(true); },
        onError: (detail) => { setPhase('idle'); onErrorRef.current?.(detail); },
        onSelectionChanged: handleSelectionChanged,
        onDataModelReady: handleDataModelReady,
        onDataModelProgress: (detail) => {
          setDataModelProgress(detail.phase === 'properties' && detail.percent >= 100 ? null : detail);
        },
        onMeasureChanged: (state) => onMeasureRef.current?.(state),
        onViewReset: () => onViewResetRef.current?.(),
        onProjectionChanged: (mode) => onProjectionRef.current?.(mode),
        onSectionChanged: (section) => onSectionRef.current?.(section),
      })
      .then((handle) => {
        if (cancelled) { handle.dispose(); return; }
        handleRef.current = handle;
        onHandleReadyRef.current?.(handle);
        // Motor remontado nasce em perspectiva e não avisa: sem isto a toolbar
        // seguiria mostrando a projeção do motor anterior.
        onProjectionRef.current?.(handle.getProjection() ?? 'perspective');
        void processUrns(); // urns já ativos ao montar
      })
      .catch((err) => {
        onErrorRef.current?.({ code: 'code-load-failed', message: String(err?.message ?? err) });
      });

    return () => {
      cancelled = true;
      handleRef.current?.dispose();
      handleRef.current = null;
      onHandleReadyRef.current?.(null);
      loadedRef.current.clear();
      dataModelStartRef.current.clear();
      setDataModelProgress(null);
    };
  }, [viewerDiv, processUrns, handleSelectionChanged, handleDataModelReady]);

  // Novos urns ativados depois do mount.
  useEffect(() => { void processUrns(); }, [urns, processUrns]);

  const fitToView = useCallback(() => handleRef.current?.fitToView(), []);

  // `handleRef` sai daqui para o `useBim3DTools`, que opera a cena pelos mesmos
  // comandos da porta — é o que permite reusar as ferramentas do viewer único
  // sem duplicar estado.
  return { loaded, phase, dataModelProgress, fitToView, handleRef };
};
